import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type TranslateLoader, type TranslationObject } from '@ngx-translate/core';
import { Observable, catchError, of, retry, timeout, timer } from 'rxjs';

/** Where the bundles are served from — mirrors the paths in ngsw-config's asset group. */
const I18N_PREFIX = '/i18n/';
const I18N_SUFFIX = '.json';

/** Two retries, backing off 300ms then 900ms, before giving up on the network. */
const RETRY_COUNT = 2;
const RETRY_BASE_DELAY_MS = 300;

/**
 * Per-attempt ceiling. HttpClient has no default timeout, and `enforceLoading: true` means the
 * app does not render until this resolves — so a request that hangs rather than fails would
 * hold the user on a blank screen indefinitely. Retrying made that worse by tripling the number
 * of requests that could hang, which is why the bound is per attempt, not overall: worst case is
 * now 3 × 5s plus 1.2s of backoff before the embedded fallback renders the app, instead of
 * unbounded.
 */
const ATTEMPT_TIMEOUT_MS = 5_000;

/**
 * Last-resort / bootstrap copy shipped inside the JS bundle.
 *
 * Used in two places:
 *   1. Seeded into ngx-translate at app init (`provideAppInitializer` in
 *      `app.config.ts`) so gate dead-ends never flash raw keys while
 *      `/i18n/<lang>.json` is still in flight on Slow 3G.
 *   2. Returned by {@link ResilientTranslateLoader} when the HTTP bundle
 *      cannot be loaded at all after retries.
 *
 * Deliberately tiny: just the strings a user would be stuck staring at if the
 * app came up untranslated. Everything else falls back to ngx-translate's
 * default behaviour of echoing the key.
 */
export const EMBEDDED_FALLBACK: TranslationObject = {
  common: {
    connectivity: {
      connectionLostTitle: 'Connection lost',
      connectionLostBody: "We are trying to reconnect — rest assured you won't lose your progress",
      noInternetTitle: 'No internet connection',
      noInternetBody: 'Check your internet connection and try again.',
      retry: 'Retry',
    },
    errors: { reloadPage: 'Reload Page' },
    toast: { dismiss: 'Dismiss' },
    // Browser dead-ends render during bootstrap, before a slow/failed bundle arrives.
    unsupportedBrowserVersion: {
      title: 'Your browser needs an update',
      body: 'Click Update below to get the latest version of {{browserName}}, then reopen {{appName}} to continue.',
      versions:
        'Your version: {{browserName}} {{currentVersion}} · Required: {{browserName}} {{minVersion}} or later',
      versionsUnknown: 'Required: {{browserName}} {{minVersion}} or later',
      update: 'Update',
    },
    unsupportedBrowser: {
      title: "Your browser isn't supported",
      body: "{{appName}} doesn't support this browser. Please open {{appName}} in a supported browser, such as Chrome, Edge, or Firefox, and try again.",
      redirect: 'Use a supported browser',
      chromeUrl: 'https://www.google.com/chrome/',
    },
    installUnsupportedBrowser: {
      title: "This browser can't install {{appName}}",
      body: '{{appName}} needs to be installed from a supported browser such as Chrome, Edge, or Brave. Please open this page in one of those browsers to continue.',
      redirect: 'Open in Chrome',
    },
  },
};

/**
 * Translation loader that cannot poison a whole session.
 *
 * The stock `provideTranslateHttpLoader` issues one GET and gives up. With no retry and no
 * fallback, a single failed or garbled `/i18n/en.json` left ngx-translate with an empty
 * dictionary, so every screen rendered raw keys until the user cleared their cache — which is
 * exactly what was reported from production. There was no path back: nothing re-requests the
 * bundle after that first failure.
 *
 * Three defences, in order:
 *  1. Retry twice with a short backoff, which covers the transient case (a flaky first request
 *     during startup, or a request racing a deploy).
 *  2. On persistent failure, fall back to {@link EMBEDDED_FALLBACK} so the UI shows words. It
 *     lives in the JS bundle, so it is available whenever the app is running at all.
 *  3. Never rethrow. `enforceLoading: true` means a rejected load blocks rendering, so an error
 *     escaping here is the difference between a partly-translated app and no app.
 *
 * Note this cannot detect the *other* half of the production failure — nginx answering a missing
 * bundle with index.html and HTTP 200, which parses as neither valid JSON nor an error. That is
 * fixed in nginx.conf, where `location ^~ /i18n/` now returns a real 404; this loader then sees
 * a proper failure and retries.
 */
@Injectable()
export class ResilientTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<TranslationObject> {
    return this.http.get<TranslationObject>(`${I18N_PREFIX}${lang}${I18N_SUFFIX}`).pipe(
      // Inside the retry, so the bound is per attempt and a hung request becomes a retryable
      // error rather than a permanently blank app.
      timeout(ATTEMPT_TIMEOUT_MS),
      retry({
        count: RETRY_COUNT,
        delay: (_error, retryIndex) => timer(RETRY_BASE_DELAY_MS * 3 ** (retryIndex - 1)),
      }),
      catchError(() => {
        // Deliberately console, not LoggerService: this runs during bootstrap, before the app's
        // own logging is necessarily usable, and a silent failure here is what made the original
        // report so hard to diagnose.
        console.error(
          `[i18n] Failed to load ${I18N_PREFIX}${lang}${I18N_SUFFIX} after ${RETRY_COUNT} retries — using the embedded fallback. The UI will show untranslated keys outside the fallback set.`,
        );
        return of(EMBEDDED_FALLBACK);
      }),
    );
  }
}
